/**
 * Final Evaluation Component
 * Displays comprehensive simulation evaluation results with charts and expandable sections
 * Styled with Tailwind CSS to match the demo UI design
 */

import React, { useRef, useState } from 'react';
import {
  ArrowLeft, Download, TrendingUp, Target, Sparkles, Award,
  ChevronDown, ChevronUp, Check, AlertCircle, X
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import clsx from 'clsx';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import UserbackWidget from '../../components/UserbackWidget';

interface SkillEvaluation {
  skill: string;
  score: number;
  trend: 'improving' | 'stable' | 'needs_attention';
  evaluation_count: number;
  positive_actions: number;
  negative_actions: number;
}

interface ObjectiveDetail {
  objective_id?: string;
  title: string;
  description: string;
  status: 'completed' | 'in_progress' | 'at_risk' | 'not_started';
  health_score: number;
  progress_percentage: number;
  related_skills?: string[];
}

interface AggregatedSkillCluster {
  skill: string;
  score: number;
  objective_count: number;
  objectives?: string[];
}

interface SkillFeedback {
  skill: string;
  went_well: string;
  could_improve: string;
}

interface ObjectiveFeedback {
  objective: string;
  went_well: string[];
  could_improve: string[];
}

interface EvaluationData {
  session_id?: string;
  evaluation_timestamp: string;
  overall_score?: number;
  candidate_name?: string;
  simulation_title?: string;
  simulation_summary: {
    total_turns: number;
    completed_objectives: number;
    total_objectives: number;
    overall_progress: number;
  };
  semantic_skill_evaluation?: SkillEvaluation[];
  objective_details?: ObjectiveDetail[];
  aggregated_skill_clusters?: AggregatedSkillCluster[];
  comprehensive_feedback?: {
    overall_summary: string;
    key_recommendations: string[];
    skill_feedback?: SkillFeedback[];
    objective_feedback?: ObjectiveFeedback[];
  };
  performance_over_time?: { turn: number; score: number }[];
}

interface FinalEvaluationProps {
  evaluation: EvaluationData | null;
  onClose: () => void;
  candidateName?: string;
  simulationTitle?: string;
}

function EvaluationSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Header Skeleton */}
      <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="h-10 w-32 bg-white/20 rounded animate-pulse"></div>
            <div className="h-10 w-36 bg-white/20 rounded animate-pulse"></div>
          </div>
          <div className="flex items-start justify-between">
            <div className="space-y-3">
              <div className="h-4 w-32 bg-white/20 rounded animate-pulse"></div>
              <div className="h-10 w-64 bg-white/20 rounded animate-pulse"></div>
              <div className="h-5 w-48 bg-white/20 rounded animate-pulse"></div>
              <div className="flex gap-4 mt-4">
                <div className="h-6 w-32 bg-white/20 rounded animate-pulse"></div>
                <div className="h-6 w-32 bg-white/20 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="w-32 h-32 bg-white/20 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-200 rounded-lg animate-pulse"></div>
                <div className="space-y-2">
                  <div className="h-8 w-16 bg-slate-200 rounded animate-pulse"></div>
                  <div className="h-4 w-24 bg-slate-200 rounded animate-pulse"></div>
                </div>
              </div>
              <div className="h-3 w-full bg-slate-200 rounded-full animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="h-6 w-40 bg-slate-200 rounded animate-pulse mb-4"></div>
              <div className="h-[300px] bg-slate-100 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Line Chart Skeleton */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="h-6 w-48 bg-slate-200 rounded animate-pulse mb-4"></div>
          <div className="h-[250px] bg-slate-100 rounded animate-pulse"></div>
        </div>

        {/* Summary Skeleton */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="h-6 w-36 bg-slate-200 rounded animate-pulse mb-4"></div>
          <div className="h-24 bg-emerald-50 rounded-lg animate-pulse"></div>
        </div>

        {/* Feedback Sections Skeleton */}
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="h-6 w-48 bg-slate-200 rounded animate-pulse mb-4"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((j) => (
                <div key={j} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-200 rounded-lg animate-pulse"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-5 w-48 bg-slate-200 rounded animate-pulse"></div>
                      <div className="h-4 w-64 bg-slate-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-6 w-20 bg-slate-200 rounded-full animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FinalEvaluation({ 
  evaluation, 
  onClose, 
  candidateName = 'Candidate',
  simulationTitle = 'Simulation'
}: FinalEvaluationProps): JSX.Element {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportToPDF = async () => {
    if (!contentRef.current) return;
    
    setIsExporting(true);
    setExportError(null);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - margin * 2);

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - margin * 2);
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `simulation-evaluation-${timestamp}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setExportError('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!evaluation) {
    return <EvaluationSkeleton />;
  }

  const { 
    simulation_summary, 
    objective_details = [], 
    semantic_skill_evaluation = [], 
    aggregated_skill_clusters = [],
    comprehensive_feedback,
    performance_over_time = [],
    overall_score,
    candidate_name: evalCandidateName,
    simulation_title: evalSimulationTitle
  } = evaluation;

  // Use evaluation data first, then props, then fallback
  const displayCandidateName = evalCandidateName || candidateName || 'Candidate';
  const displaySimulationTitle = evalSimulationTitle || simulationTitle || 'Simulation';

  const filteredSkills = semantic_skill_evaluation.filter((skill) => {
    const skillLower = skill.skill.toLowerCase();
    return !skillLower.includes('simulation goal') && 
           !skillLower.includes('primary goal') &&
           !skillLower.includes('main goal');
  });

  const calculatedOverallScore = overall_score ?? (
    filteredSkills.length > 0 
      ? Math.round((filteredSkills.reduce((sum, s) => sum + s.score, 0) / filteredSkills.length - 1) * (100 / 9))
      : Math.round((simulation_summary?.overall_progress || 0) * 100)
  );

  const radarData = filteredSkills.map(skill => ({
    skill: skill.skill.length > 15 ? skill.skill.substring(0, 15) + '...' : skill.skill,
    score: skill.score,
    fullMark: 10
  }));

  const objectivesData = objective_details.map(obj => ({
    name: obj.title.length > 20 ? obj.title.substring(0, 20) + '...' : obj.title,
    progress: Math.round(obj.progress_percentage),
    health: Math.round(obj.health_score * 10)
  }));

  const competencyRadarData = aggregated_skill_clusters.map(cluster => ({
    skill: cluster.skill.length > 15 ? cluster.skill.substring(0, 15) + '...' : cluster.skill,
    score: cluster.score,
    fullMark: 10
  }));

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-emerald-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 90) return 'bg-green-50 border-green-200';
    if (score >= 80) return 'bg-emerald-50 border-emerald-200';
    if (score >= 70) return 'bg-yellow-50 border-yellow-200';
    if (score >= 60) return 'bg-orange-50 border-orange-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100';
      case 'in_progress': return 'bg-blue-100';
      case 'at_risk': return 'bg-orange-100';
      default: return 'bg-slate-100';
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === 'completed') {
      return (
        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    return null;
  };

  const avgSkillScore = filteredSkills.length > 0
    ? (filteredSkills.reduce((sum, s) => sum + s.score, 0) / filteredSkills.length).toFixed(1)
    : '0.0';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* PDF Content Wrapper */}
      <div ref={contentRef} style={{ backgroundColor: '#f8fafc' }}>
        {/* Unified Header with Gradient */}
        <div 
          style={{ 
            background: 'linear-gradient(to bottom right, #10b981, #059669, #0d9488)',
            padding: '24px',
            color: 'white'
          }}
        >
          <div style={{ maxWidth: '1152px', margin: '0 auto' }}>
            {/* Navigation Row - Hidden in PDF */}
            <div className="flex items-center justify-between mb-6 print:hidden">
              <button
                onClick={onClose}
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Back to Results</span>
              </button>
              <button 
                onClick={exportToPDF}
                disabled={isExporting}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-700 hover:bg-emerald-50 rounded-lg font-semibold transition-colors disabled:opacity-50 shadow-sm"
              >
                <Download className="w-4 h-4" />
                {isExporting ? 'Exporting...' : 'Export Report'}
              </button>
            </div>

            {/* Header Content */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: '#a7f3d0', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>Final Evaluation Report</div>
                <h1 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', color: 'white' }}>{displaySimulationTitle}</h1>
                <p style={{ color: 'white', marginBottom: '16px', fontSize: '18px', opacity: 0.9 }}>{displayCandidateName}</p>
                <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.15)', padding: '6px 12px', borderRadius: '6px' }}>
                    <Target className="w-4 h-4" style={{ color: 'white' }} />
                    <span>{simulation_summary?.completed_objectives || 0}/{simulation_summary?.total_objectives || 0} Objectives</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(255,255,255,0.15)', padding: '6px 12px', borderRadius: '6px' }}>
                    <TrendingUp className="w-4 h-4" style={{ color: 'white' }} />
                    <span>{simulation_summary?.total_turns || 0} Interactions</span>
                  </div>
                </div>
              </div>
              <div style={{ 
                textAlign: 'center', 
                padding: '24px 32px', 
                borderRadius: '16px', 
                border: '2px solid #bbf7d0',
                backgroundColor: 'white',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ 
                  fontSize: '52px', 
                  fontWeight: 700, 
                  marginBottom: '4px',
                  color: calculatedOverallScore >= 80 ? '#059669' : calculatedOverallScore >= 60 ? '#ca8a04' : '#dc2626'
                }}>
                  {calculatedOverallScore}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>Overall Score</div>
              </div>
            </div>
          </div>
        </div>

        {/* Export Error Alert - Outside PDF content */}
        {exportError && (
          <div className="max-w-6xl mx-auto px-6 pt-4 print:hidden">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                <span>{exportError}</span>
              </div>
              <button onClick={() => setExportError(null)}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div style={{ maxWidth: '1152px', margin: '0 auto', padding: '32px 24px' }} className="space-y-6">
        {/* Metric Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#d1fae5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Award className="w-5 h-5" style={{ color: '#059669' }} />
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a' }}>
                  {Math.round((simulation_summary?.overall_progress || 0) * 100)}%
                </div>
                <div style={{ fontSize: '14px', color: '#475569' }}>Overall Progress</div>
              </div>
            </div>
            <div style={{ width: '100%', backgroundColor: '#e2e8f0', borderRadius: '9999px', height: '12px' }}>
              <div
                style={{ 
                  backgroundColor: '#10b981', 
                  height: '12px', 
                  borderRadius: '9999px',
                  width: `${(simulation_summary?.overall_progress || 0) * 100}%`
                }}
              />
            </div>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#dbeafe', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Target className="w-5 h-5" style={{ color: '#2563eb' }} />
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a' }}>
                  {simulation_summary?.completed_objectives || 0}
                </div>
                <div style={{ fontSize: '14px', color: '#475569' }}>Objectives Completed</div>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              Out of {simulation_summary?.total_objectives || 0} total objectives
            </div>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#f3e8ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles className="w-5 h-5" style={{ color: '#9333ea' }} />
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a' }}>{avgSkillScore}</div>
                <div style={{ fontSize: '14px', color: '#475569' }}>Avg Skill Score</div>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              Across {filteredSkills.length} competencies
            </div>
          </div>
        </div>

        {/* Overall Summary - At the top */}
        {comprehensive_feedback?.overall_summary && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>Overall Summary</h3>
            <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '16px' }}>
              <p style={{ color: '#1e293b', lineHeight: 1.625 }}>{comprehensive_feedback.overall_summary}</p>
            </div>
          </div>
        )}

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Radar Chart - Skills Assessment */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>Skills Assessment</h3>
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <Radar name="Score" dataKey="score" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                No skill data available
              </div>
            )}
          </div>

          {/* Bar Chart - Objectives Progress */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>Objectives Progress</h3>
            {objectivesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={objectivesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} angle={-15} textAnchor="end" height={80} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="progress" fill="#10b981" name="Progress %" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                No objective data available
              </div>
            )}
          </div>
        </div>

        {/* Core Competency Assessment Radar Chart */}
        {competencyRadarData.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>Core Competency Assessment</h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
              Aggregated skill scores across all objectives ({aggregated_skill_clusters.length} competencies evaluated)
            </p>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={competencyRadarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 12 }} />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Radar name="Competency Score" dataKey="score" stroke="#9333ea" fill="#9333ea" fillOpacity={0.5} />
                <Tooltip 
                  formatter={(value: number) => [`${value.toFixed(1)}/10`, 'Score']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                />
              </RadarChart>
            </ResponsiveContainer>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {aggregated_skill_clusters.map((cluster, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200"
                >
                  <span className="text-sm font-medium text-purple-700">{cluster.skill}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Over Time Line Chart */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>Performance Over Time</h3>
          {performance_over_time.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={performance_over_time}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="turn" 
                  label={{ value: 'Interaction Turn', position: 'insideBottom', offset: -5 }} 
                  tick={{ fill: '#64748b' }} 
                />
                <YAxis domain={[0, 100]} tick={{ fill: '#64748b' }} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              Performance data will be available after completing more interactions
            </div>
          )}
        </div>

        {/* Detailed Skill Feedback - Expandable */}
        {filteredSkills.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>Detailed Skill Feedback</h3>
            <div className="space-y-4">
              {filteredSkills.map((skill, index) => {
                const skillFeedback = comprehensive_feedback?.skill_feedback?.find(
                  (f) => f.skill.toLowerCase().includes(skill.skill.toLowerCase().slice(0, 15))
                );

                return (
                  <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedSection(expandedSection === `skill-${index}` ? null : `skill-${index}`)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <span className="text-lg font-bold text-emerald-600">{skill.score.toFixed(1)}</span>
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-semibold text-slate-900">{skill.skill}</div>
                          <div className="text-sm text-slate-600">
                            {skill.evaluation_count} evaluations | {skill.positive_actions} positive actions
                          </div>
                        </div>
                        <div className={clsx('px-3 py-1 rounded-full text-sm font-medium', 
                          skill.trend === 'improving' ? 'bg-green-100 text-green-700' :
                          skill.trend === 'needs_attention' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                        )}>
                          {skill.trend.replace('_', ' ')}
                        </div>
                      </div>
                      {expandedSection === `skill-${index}` ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </button>

                    {expandedSection === `skill-${index}` && skillFeedback && (
                      <div className="p-4 bg-slate-50 border-t border-slate-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <h4 className="font-semibold text-slate-900 text-sm">What Went Well</h4>
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed">
                              {skillFeedback.went_well || 'No specific feedback available'}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                              <h4 className="font-semibold text-slate-900 text-sm">Could Be Improved</h4>
                            </div>
                            <p className="text-sm text-slate-700 leading-relaxed">
                              {skillFeedback.could_improve || 'No specific feedback available'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {expandedSection === `skill-${index}` && !skillFeedback && (
                      <div className="p-4 bg-slate-50 border-t border-slate-200 text-center text-slate-500 text-sm">
                        Detailed feedback not available for this skill
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Detailed Objectives Feedback - Expandable */}
        {objective_details.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>Detailed Objectives Feedback</h3>
            <div className="space-y-4">
              {objective_details.map((objective, index) => {
                const objFeedback = comprehensive_feedback?.objective_feedback?.find(
                  (f) => f.objective.toLowerCase().includes(objective.title.toLowerCase().slice(0, 20))
                );

                return (
                  <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedSection(expandedSection === `obj-${index}` ? null : `obj-${index}`)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', getStatusBg(objective.status))}>
                          {objective.status === 'completed' ? (
                            getStatusIcon(objective.status)
                          ) : (
                            <span className="text-sm font-bold text-slate-600">{Math.round(objective.progress_percentage)}%</span>
                          )}
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-semibold text-slate-900">{objective.title}</div>
                          <div className="text-sm text-slate-600">{objective.description}</div>
                          {objective.related_skills && objective.related_skills.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {objective.related_skills.map((skill, skillIdx) => (
                                <span 
                                  key={skillIdx}
                                  className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full"
                                >
                                  {skill.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {expandedSection === `obj-${index}` ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </button>

                    {expandedSection === `obj-${index}` && objFeedback && (
                      <div className="p-4 bg-slate-50 border-t border-slate-200">
                        {objective.related_skills && objective.related_skills.length > 0 && (
                          <div className="mb-4 pb-4 border-b border-slate-200">
                            <h4 className="font-semibold text-slate-900 text-sm mb-2">Related Skills Evaluated</h4>
                            <div className="flex flex-wrap gap-2">
                              {objective.related_skills.map((skill, skillIdx) => (
                                <span 
                                  key={skillIdx}
                                  className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-700 rounded-lg"
                                >
                                  {skill.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <h4 className="font-semibold text-slate-900 text-sm">What Went Well</h4>
                            </div>
                            {objFeedback.went_well && objFeedback.went_well.length > 0 ? (
                              <ul className="space-y-1">
                                {objFeedback.went_well.map((item, i) => (
                                  <li key={i} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-slate-500">No specific feedback available</p>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                              <h4 className="font-semibold text-slate-900 text-sm">Could Be Improved</h4>
                            </div>
                            {objFeedback.could_improve && objFeedback.could_improve.length > 0 ? (
                              <ul className="space-y-1">
                                {objFeedback.could_improve.map((item, i) => (
                                  <li key={i} className="text-sm text-slate-700 leading-relaxed flex items-start gap-2">
                                    <span className="text-orange-500 mt-0.5 flex-shrink-0">→</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-slate-500">No specific feedback available</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {expandedSection === `obj-${index}` && !objFeedback && (
                      <div className="p-4 bg-slate-50 border-t border-slate-200">
                        {objective.related_skills && objective.related_skills.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-slate-900 text-sm mb-2">Related Skills Evaluated</h4>
                            <div className="flex flex-wrap gap-2">
                              {objective.related_skills.map((skill, skillIdx) => (
                                <span 
                                  key={skillIdx}
                                  className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-700 rounded-lg"
                                >
                                  {skill.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="text-center text-slate-500 text-sm">
                          Detailed feedback not available for this objective
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Key Recommendations */}
        {comprehensive_feedback?.key_recommendations && comprehensive_feedback.key_recommendations.length > 0 && (
          <div style={{ 
            background: 'linear-gradient(to bottom right, #faf5ff, #eff6ff)', 
            border: '1px solid #e9d5ff', 
            borderRadius: '12px', 
            padding: '24px' 
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles className="w-5 h-5" style={{ color: '#9333ea' }} />
              Key Recommendations
            </h3>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {comprehensive_feedback.key_recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span style={{ 
                    width: '24px', 
                    height: '24px', 
                    backgroundColor: '#a855f7', 
                    color: 'white', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '12px', 
                    fontWeight: 700,
                    flexShrink: 0
                  }}>
                    {index + 1}
                  </span>
                  <span style={{ color: '#334155', lineHeight: 1.625 }}>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>
      </div>

      <UserbackWidget />
    </div>
  );
}
